import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { Badge } from "src/components/ui/badge";
import { Checkbox } from "src/components/ui/checkbox";
import { Label } from "src/components/ui/label";
import { ScrollArea } from "src/components/ui/scroll-area";
import { Separator } from "src/components/ui/separator";
import { Network, ZoomIn, ZoomOut, Users, FilterX } from "lucide-react";

const SCORE_CATEGORIES = {
  absoluteAcceptance: {
    label: "Absolútny súhlas (5)",
    range: [5, 5],
    color: "#22c55e",
  },
  acceptance: {
    label: "Súhlas s výhradami (3-4)",
    range: [3, 4],
    color: "#eab308",
  },
  disagreement: { label: "Nesúhlas (1-2)", range: [1, 2], color: "#ef4444" },
  absoluteDisagreement: {
    label: "Absolútny nesúhlas (0)",
    range: [0, 0],
    color: "#1f2937",
  },
};

// Constants for node sizing and spacing
const NODE_RADIUS = 35;
const OUTER_RADIUS = NODE_RADIUS + 4;
const ARROW_OFFSET = 25;
const LABEL_OFFSET = 12;

const getScoreColor = (score) => {
  if (score === 5) return SCORE_CATEGORIES.absoluteAcceptance.color;
  if (score >= 3) return SCORE_CATEGORIES.acceptance.color;
  if (score >= 1) return SCORE_CATEGORIES.disagreement.color;
  return SCORE_CATEGORIES.absoluteDisagreement.color; // For score 0
};

const EvaluationRelationships = ({ matrixData }) => {
  const svgRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [highlightedEdges, setHighlightedEdges] = useState(new Set());
  const [scale, setScale] = useState(1);
  const [selectedCategories, setSelectedCategories] = useState(
    Object.keys(SCORE_CATEGORIES)
  );
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [showScores, setShowScores] = useState(false);
  const [visibleUsers, setVisibleUsers] = useState(new Set());
  const simulationRef = useRef(null);

  useEffect(() => {
    if (!matrixData?.length) return;

    const employees = matrixData.filter((row) => !row.isAI && !row.isAverage);

    // Initialize visible users if empty
    if (visibleUsers.size === 0) {
      setVisibleUsers(new Set(employees.map((emp) => emp.id)));
    }

    // Process nodes
    const processedNodes = employees
      .filter((emp) => visibleUsers.has(emp.id))
      .map((emp) => ({
        id: emp.id,
        name: emp.name,
        // More concise using nullish coalescing operator
        selfScore: emp[emp.id] ?? null,
      }));

    // Process edges

    const processedEdges = [];
    employees.forEach((evaluator) => {
      if (!visibleUsers.has(evaluator.id)) return;

      employees.forEach((evaluated) => {
        if (
          !visibleUsers.has(evaluated.id) ||
          evaluator.id === evaluated.id ||
          evaluator[evaluated.id] === null
        )
          return;

        // Reverse the direction: now target is the evaluator (who gave the score)
        processedEdges.push({
          id: `${evaluated.id}-${evaluator.id}`, // Changed order
          source: evaluated.id, // Changed from evaluator.id
          target: evaluator.id, // Changed from evaluated.id
          score: evaluator[evaluated.id],
          evaluatorName: evaluator.name,
          evaluatedName: evaluated.name,
        });
      });
    });

    // Set up force simulation
    const width = 1000;
    const height = 600;

    if (simulationRef.current) {
      simulationRef.current.stop();
    }

    const simulation = d3
      .forceSimulation(processedNodes)
      .force(
        "link",
        d3
          .forceLink(processedEdges)
          .id((d) => d.id)
          .distance(250)
      )
      .force("charge", d3.forceManyBody().strength(-1500))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(OUTER_RADIUS * 2))
      // Add these lines to make it instant:
      .alpha(1) // Set initial alpha high
      .alphaDecay(1) // Make it decay immediately
      .alphaMin(0) // Allow it to go to complete rest
      .stop(); // Stop the automatic ticking

    // Force the simulation to complete immediately
    for (let i = 0; i < 100; ++i) simulation.tick(); // Run 100 ticks instantly

    // Update positions immediately
    setNodes([...processedNodes]);
    setEdges([...processedEdges]);

    simulationRef.current = simulation;

    return () => simulation.stop();
  }, [matrixData, visibleUsers]);

  const calculateEdgePath = (edge) => {
    const sourceNode = nodes.find((n) => n.id === edge.source.id);
    const targetNode = nodes.find((n) => n.id === edge.target.id);

    if (!sourceNode || !targetNode) return "";

    const dx = targetNode.x - sourceNode.x;
    const dy = targetNode.y - sourceNode.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Calculate offset points
    const sourceOffset = OUTER_RADIUS + ARROW_OFFSET;
    const targetOffset = OUTER_RADIUS + ARROW_OFFSET;

    // Check if there's a reverse edge
    const hasReverseEdge = edges.some(
      (e) =>
        e.source.id === edge.target.id &&
        e.target.id === edge.source.id &&
        e.id < edge.id // Only offset one direction
    );

    // Add a small offset if this is the second edge between these nodes
    const offset = hasReverseEdge ? 16 : 18; // 8px offset, adjust as needed

    const sourceX =
      sourceNode.x +
      (dx * sourceOffset) / distance +
      (hasReverseEdge ? (dy / distance) * offset : 0);
    const sourceY =
      sourceNode.y +
      (dy * sourceOffset) / distance +
      (hasReverseEdge ? (-dx / distance) * offset : 0);
    const targetX =
      targetNode.x -
      (dx * targetOffset) / distance +
      (hasReverseEdge ? (dy / distance) * offset : 0);
    const targetY =
      targetNode.y -
      (dy * targetOffset) / distance +
      (hasReverseEdge ? (-dx / distance) * offset : 0);

    // Add a small curve with control point slightly above the line
    const midX = (sourceX + targetX) / 2;
    const midY = (sourceY + targetY) / 2 - 10; // Small curve up by 10px

    return `M ${sourceX} ${sourceY} Q ${midX} ${midY} ${targetX} ${targetY}`;
  };

  const Controls = () => {
    const employees = matrixData.filter((row) => !row.isAI && !row.isAverage);

    const toggleAllUsers = (checked) => {
      if (checked) {
        setVisibleUsers(new Set(employees.map((emp) => emp.id)));
      } else {
        setVisibleUsers(new Set());
      }
      setSelectedNode(null);
      setHighlightedEdges(new Set());
    };

    return (
      <div className="absolute top-4 left-4 space-y-4 bg-white p-4 rounded-lg shadow-lg border z-50 max-w-xs">
        <div className="space-y-3">
          <div className="text-sm font-medium flex items-center justify-between">
            <span>Zobraziť hodnotenia</span>
          </div>
          {Object.entries(SCORE_CATEGORIES).map(([key, { label, color }]) => (
            <div key={key} className="flex items-center space-x-2">
              <Checkbox
                id={key}
                checked={selectedCategories.includes(key)}
                onCheckedChange={(checked) => {
                  setSelectedCategories((prev) =>
                    checked ? [...prev, key] : prev.filter((cat) => cat !== key)
                  );
                }}
              />
              <label
                htmlFor={key}
                className="text-sm flex items-center space-x-2 cursor-pointer"
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span>{label}</span>
              </label>
            </div>
          ))}
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Používatelia</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() =>
                toggleAllUsers(visibleUsers.size !== employees.length)
              }
            >
              {visibleUsers.size === employees.length ? (
                <FilterX className="w-4 h-4" />
              ) : (
                <Users className="w-4 h-4" />
              )}
            </Button>
          </div>
          <ScrollArea className="h-40">
            <div className="space-y-2">
              {employees.map((emp) => (
                <div key={emp.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`user-${emp.id}`}
                    checked={visibleUsers.has(emp.id)}
                    onCheckedChange={(checked) => {
                      setVisibleUsers((prev) => {
                        const newSet = new Set(prev);
                        if (checked) {
                          newSet.add(emp.id);
                        } else {
                          newSet.delete(emp.id);
                        }
                        return newSet;
                      });
                    }}
                  />
                  <Label
                    htmlFor={`user-${emp.id}`}
                    className="text-sm cursor-pointer truncate"
                  >
                    {emp.name}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <Separator />

        <div className="flex items-center space-x-2">
          <Checkbox
            id="showScores"
            checked={showScores}
            onCheckedChange={setShowScores}
          />
          <Label htmlFor="showScores" className="text-sm cursor-pointer">
            Zobraziť čísla hodnotení
          </Label>
        </div>

        <div className="flex space-x-2 pt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setScale((s) => Math.min(s + 0.1, 2))}
            title="Priblížiť"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setScale((s) => Math.max(s - 0.1, 0.5))}
            title="Oddialiť"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  const filteredEdges = edges.filter((edge) => {
    const score = edge.score;
    return selectedCategories.some((category) => {
      const { range } = SCORE_CATEGORIES[category];
      return score >= range[0] && score <= range[1];
    });
  });

  const handleNodeClick = (node) => {
    if (selectedNode?.id === node.id) {
      setSelectedNode(null);
      setHighlightedEdges(new Set());
    } else {
      setSelectedNode(node);
      const relatedEdges = edges
        .filter((e) => e.source.id === node.id || e.target.id === node.id)
        .map((e) => e.id);
      setHighlightedEdges(new Set(relatedEdges));
    }
  };

  if (!matrixData?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Network className="h-5 w-5" />
            Vzťahy hodnotení
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[600px] flex items-center justify-center text-gray-500">
          Nie sú dostupné žiadne údaje na vizualizáciu
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Network className="h-5 w-5" />
          Vzťahy hodnotení
        </CardTitle>
        <CardDescription>
          Interaktívna vizualizácia vzťahov hodnotení medzi členmi tímu.
        </CardDescription>
      </CardHeader>
      <CardContent className="relative h-[600px] overflow-hidden">
        <Controls />

        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox="0 0 1000 600"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "center",
            transition: "transform 0.2s ease-out",
          }}
        >
          <defs>
            {Object.values(SCORE_CATEGORIES).map((category) => (
              <marker
                key={category.color}
                id={`arrow-${category.color.replace("#", "")}`}
                viewBox="0 0 10 10"
                refX="5"
                refY="5"
                markerWidth="8"
                markerHeight="8"
                orient="auto"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={category.color} />
              </marker>
            ))}
          </defs>

          <g className="edges">
            // Inside filteredEdges.map in EvaluationRelationships.js
            {filteredEdges.map((edge) => {
              const path = calculateEdgePath(edge);
              const isHighlighted = highlightedEdges.has(edge.id);
              const color = getScoreColor(edge.score);

              return (
                <g
                  key={edge.id}
                  className="group"
                  onMouseEnter={() => setHoveredEdge(edge)}
                  onMouseLeave={() => setHoveredEdge(null)}
                >
                  {/* Invisible wider path for better hover detection */}
                  <path
                    d={path}
                    stroke="transparent"
                    strokeWidth="12"
                    fill="none"
                    className="cursor-pointer"
                  />

                  {/* Visible edge path */}
                  <path
                    d={path}
                    stroke={color}
                    strokeWidth={isHighlighted ? "3" : "1.5"}
                    fill="none"
                    opacity={isHighlighted || !selectedNode ? "1" : "0.2"}
                    markerEnd={`url(#arrow-${color.replace("#", "")})`}
                    className="transition-all duration-200 group-hover:stroke-[2.5px]"
                  />

                  {showScores && (
                    <>
                      <path id={`scorePath-${edge.id}`} d={path} fill="none" />
                      <text
                        className={`text-sm fill-current ${
                          isHighlighted || !selectedNode
                            ? "opacity-100"
                            : "opacity-20"
                        }`}
                      >
                        <textPath
                          href={`#scorePath-${edge.id}`}
                          startOffset="50%"
                          textAnchor="middle"
                          className="[dominant-baseline:text-after-edge] translate-y-2"
                        >
                          <tspan dy="-1.5">{edge.score}</tspan>
                        </textPath>
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </g>

          {nodes.map((node) => {
            const isSelected = selectedNode?.id === node.id;
            const isConnected =
              selectedNode &&
              edges.some(
                (e) =>
                  (e.source.id === node.id &&
                    e.target.id === selectedNode.id) ||
                  (e.target.id === node.id && e.source.id === selectedNode.id)
              );

            return (
              <g
                key={node.id}
                transform={`translate(${node.x || 0},${node.y || 0})`}
                onClick={() => handleNodeClick(node)}
                onMouseEnter={() => setHoveredNode(node)}
                onMouseLeave={() => setHoveredNode(null)}
                className="cursor-pointer"
              >
                {/* Self-assessment circle */}
                {node.selfScore !== null && (
                  <circle
                    r={OUTER_RADIUS + 4}
                    fill="none"
                    stroke={getScoreColor(node.selfScore)}
                    strokeWidth="2"
                    opacity={
                      isSelected || isConnected || !selectedNode ? "1" : "0.2"
                    }
                    className="transition-all duration-200"
                  />
                )}

                {/* Main node circle */}
                <circle
                  r={NODE_RADIUS}
                  fill={isSelected ? "#e2e8f0" : "white"}
                  stroke={isSelected ? "#3b82f6" : "#e2e8f0"}
                  strokeWidth={isSelected ? "3" : "1"}
                  opacity={
                    isSelected || isConnected || !selectedNode ? "1" : "0.2"
                  }
                  className="transition-all duration-200"
                />

                {/* Node label */}
                <text
                  textAnchor="middle"
                  dy="0.35em"
                  className={`text-sm font-medium ${
                    isSelected ? "fill-blue-600" : "fill-gray-700"
                  } ${
                    isSelected || isConnected || !selectedNode
                      ? "opacity-100"
                      : "opacity-20"
                  } transition-all duration-200`}
                >
                  {node.name}
                </text>

                {/* Self-score badge */}
                {node.selfScore !== null && (
                  <g
                    transform={`translate(${NODE_RADIUS - 8}, -${
                      NODE_RADIUS - 8
                    })`}
                  >
                    <circle
                      r="12"
                      fill="white"
                      stroke={getScoreColor(node.selfScore)}
                      strokeWidth="1.5"
                    />
                    <text
                      textAnchor="middle"
                      dy="0.35em"
                      className="text-sm font-bold"
                      fill={getScoreColor(node.selfScore)}
                    >
                      {node.selfScore}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        {/* Edge tooltip */}
        {hoveredEdge && (
          <div
            className="absolute p-3 bg-white rounded-lg shadow-lg border border-gray-200 pointer-events-none z-50"
            style={{
              left: hoveredEdge.source.x
                ? (hoveredEdge.target.x + hoveredEdge.source.x) / 2
                : 0,
              top: hoveredEdge.source.y
                ? (hoveredEdge.target.y + hoveredEdge.source.y) / 2 - 60
                : 0,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-50">
                  {hoveredEdge.evaluatedName} {/* Changed order */}
                </Badge>
                <span className="text-sm text-gray-500">ohodnotil</span>
                <Badge variant="outline" className="bg-green-50">
                  {hoveredEdge.evaluatorName} {/* Changed order */}
                </Badge>
              </div>
              <div className="text-center">
                <span className="text-sm text-gray-500">Hodnotenie:</span>
                <span
                  className="ml-2 font-bold"
                  style={{ color: getScoreColor(hoveredEdge.score) }}
                >
                  {hoveredEdge.score}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Node tooltip */}
        {hoveredNode && (
          <div
            className="absolute p-3 bg-white rounded-lg shadow-lg border border-gray-200 pointer-events-none z-50"
            style={{
              left: hoveredNode.x || 0,
              top: (hoveredNode.y || 0) - 80,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div className="space-y-2">
              <div className="font-medium text-gray-900">
                {hoveredNode.name}
              </div>
              {hoveredNode.selfScore !== null ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Sebahodnotenie:</span>
                  <Badge
                    variant="outline"
                    style={{
                      backgroundColor: `${getScoreColor(
                        hoveredNode.selfScore
                      )}15`,
                      color: getScoreColor(hoveredNode.selfScore),
                      borderColor: getScoreColor(hoveredNode.selfScore),
                    }}
                  >
                    {hoveredNode.selfScore}
                  </Badge>
                </div>
              ) : (
                <div className="text-sm text-gray-500">Bez sebahodnotenia</div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EvaluationRelationships;
